import { useState, useEffect, useCallback } from 'react'
import { motion } from 'framer-motion'
import {
  Database,
  HardDrive,
  Calendar,
  Clock,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Archive,
  CalendarDays,
  CalendarRange,
  Play,
  UploadCloud,
  Timer,
  FolderArchive,
  Tag,
  FileText,
  Cpu,
  MemoryStick,
  Activity
} from 'lucide-react'
import { backupApi, serverHealthApi } from '../services/api'
import './BackupStats.css'

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.4, 0, 0.2, 1] }
  }
}

function getHealthColor(percent) {
  if (percent >= 90) return 'health-critical'
  if (percent >= 70) return 'health-warning'
  return 'health-good'
}

function HealthCard({ icon: Icon, label, percent, detail }) {
  const colorClass = getHealthColor(percent)
  return (
    <div className={`health-card ${colorClass}`}>
      <div className="health-card-top">
        <div className="health-card-icon"><Icon size={18} /></div>
        <div className="health-card-info">
          <span className="health-card-label">{label}</span>
          <span className="health-card-detail">{detail}</span>
        </div>
        <span className="health-card-percent">{percent}%</span>
      </div>
      <div className="health-card-bar">
        <div className="health-card-bar-fill" style={{ width: `${Math.min(percent, 100)}%` }} />
      </div>
    </div>
  )
}

function BackupStats() {
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [triggering, setTriggering] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [backupName, setBackupName] = useState('')
  const [showBackupModal, setShowBackupModal] = useState(false)
  const [health, setHealth] = useState(null)

  const loadHealth = useCallback(async () => {
    try {
      const response = await serverHealthApi.getStats()
      if (response.data?.success) {
        setHealth(response.data.data)
      }
    } catch (err) {
      console.error('Failed to load server health:', err)
    }
  }, [])

  useEffect(() => {
    loadData()
    loadHealth()
    const healthInterval = setInterval(loadHealth, 30000)
    return () => clearInterval(healthInterval)
  }, [loadHealth])

  const loadData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true)
    else setLoading(true)
    setError(null)

    try {
      const response = await backupApi.getStats()
      if (response.data.success) {
        setData(response.data.data)
      } else {
        setError(response.data.error || 'Failed to load backup stats')
      }
    } catch (err) {
      console.error('Failed to load backup stats:', err)
      setError(err.message || 'Failed to load backup stats')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const triggerBackup = async () => {
    setTriggering(true)
    setShowBackupModal(false)
    try {
      const response = await backupApi.trigger(backupName.trim() || null)
      if (response.data.success) {
        // Reload data after backup
        await loadData(true)
        setBackupName('')
      } else {
        setError(response.data.error || 'Failed to trigger backup')
      }
    } catch (err) {
      console.error('Failed to trigger backup:', err)
      setError(err.message || 'Failed to trigger backup')
    } finally {
      setTriggering(false)
    }
  }

  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const formatTime = (dateStr) => {
    const date = new Date(dateStr)
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatRelativeTime = (dateStr) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins} min ago`
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
  }

  if (loading) {
    return (
      <div className="backup-stats">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading backup statistics...</p>
        </div>
      </div>
    )
  }

  if (error && !data) {
    return (
      <div className="backup-stats">
        <div className="error-container">
          <AlertCircle size={48} />
          <h2>Failed to Load Backup Stats</h2>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => loadData()}>
            <RefreshCw size={16} /> Try Again
          </button>
        </div>
      </div>
    )
  }

  const { summary, daily, monthly, manual } = data || {}

  return (
    <motion.div
      className="backup-stats"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <motion.div className="backup-header" variants={itemVariants}>
        <div className="backup-title-section">
          <div className="backup-title-icon">
            <UploadCloud size={24} />
          </div>
          <div className="backup-title-text">
            <h1>Database Backups</h1>
            <p>Azure Blob Storage backup status and history</p>
          </div>
        </div>
        <div className="backup-actions">
          <button
            className="btn btn-secondary"
            onClick={() => loadData(true)}
            disabled={refreshing}
          >
            <RefreshCw size={16} className={refreshing ? 'spin' : ''} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setShowBackupModal(true)}
            disabled={triggering}
          >
            <Play size={16} />
            {triggering ? 'Running...' : 'Run Backup Now'}
          </button>
        </div>
      </motion.div>

      {/* Server Health Stats */}
      {health && (
        <motion.div className="health-section" variants={itemVariants}>
          <div className="section-header health-header">
            <h2><Activity size={20} /> Server Health</h2>
            <span className="health-uptime">Uptime: {health.uptime}</span>
          </div>
          <div className="health-grid">
            <HealthCard
              icon={Cpu}
              label="CPU"
              percent={health.cpu?.percent ?? 0}
              detail={`${health.cpu?.cores || '?'} cores`}
            />
            <HealthCard
              icon={MemoryStick}
              label="RAM"
              percent={health.ram?.percent ?? 0}
              detail={`${health.ram?.used || '?'} / ${health.ram?.total || '?'}`}
            />
            {health.disks?.map(disk => (
              <HealthCard
                key={disk.mount}
                icon={HardDrive}
                label={disk.mount === '/' ? 'Disk 1 (/)' : `Disk 2 (${disk.mount})`}
                percent={disk.percent ?? 0}
                detail={`${disk.used} / ${disk.size}`}
              />
            ))}
          </div>
        </motion.div>
      )}

      {/* Stats Cards */}
      <motion.div className="stat-blocks" variants={itemVariants}>
        <div className="stat-block green">
          <div className="stat-block-icon"><CheckCircle2 size={20} /></div>
          <div className="stat-block-content">
            <div className="stat-block-value">{summary?.lastBackup ? formatRelativeTime(summary.lastBackup.date) : 'Never'}</div>
            <div className="stat-block-label">Last Backup</div>
          </div>
        </div>

        <div className="stat-block blue">
          <div className="stat-block-icon"><CalendarDays size={20} /></div>
          <div className="stat-block-content">
            <div className="stat-block-value">{summary?.totalDailyBackups || 0}</div>
            <div className="stat-block-label">Daily Backups</div>
          </div>
        </div>

        <div className="stat-block purple">
          <div className="stat-block-icon"><CalendarRange size={20} /></div>
          <div className="stat-block-content">
            <div className="stat-block-value">{summary?.totalMonthlyBackups || 0}</div>
            <div className="stat-block-label">Monthly Backups</div>
          </div>
        </div>

        <div className="stat-block cyan">
          <div className="stat-block-icon"><Tag size={20} /></div>
          <div className="stat-block-content">
            <div className="stat-block-value">{summary?.totalManualBackups || 0}</div>
            <div className="stat-block-label">Manual Backups</div>
          </div>
        </div>

        <div className="stat-block orange">
          <div className="stat-block-icon"><HardDrive size={20} /></div>
          <div className="stat-block-content">
            <div className="stat-block-value">{summary?.totalStorageUsed || '0 Bytes'}</div>
            <div className="stat-block-label">Storage Used</div>
          </div>
        </div>
      </motion.div>

      {/* Backup Tables */}
      <div className="backup-tables">
        {/* Daily Backups */}
        <motion.div className="backup-section" variants={itemVariants}>
          <div className="section-header">
            <h2><CalendarDays size={20} /> Daily Backups</h2>
            <span className="badge badge-blue">{daily?.length || 0} backups</span>
          </div>
          <div className="table-container">
            <table className="backup-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Databases</th>
                  <th>Total Size</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {daily && daily.length > 0 ? (
                  daily.map((backup, index) => (
                    <tr key={backup.date || index}>
                      <td className="date-cell">
                        <Calendar size={14} />
                        {formatDate(backup.lastModified)}
                      </td>
                      <td className="time-cell">
                        <Clock size={14} />
                        {formatTime(backup.lastModified)}
                      </td>
                      <td className="databases-cell">
                        {backup.databases.map((db, i) => (
                          <span key={i} className="db-badge">
                            <Database size={12} />
                            {db.name} ({db.sizeFormatted})
                          </span>
                        ))}
                      </td>
                      <td className="size-cell">{backup.totalSizeFormatted}</td>
                      <td className="status-cell">
                        <span className="status-badge status-success">
                          <CheckCircle2 size={14} /> Complete
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="empty-cell">
                      <Archive size={24} />
                      <span>No daily backups found</span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Monthly Backups */}
        <motion.div className="backup-section" variants={itemVariants}>
          <div className="section-header">
            <h2><CalendarRange size={20} /> Monthly Backups</h2>
            <span className="badge badge-purple">{monthly?.length || 0} backups</span>
          </div>
          <div className="table-container">
            <table className="backup-table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Created</th>
                  <th>Databases</th>
                  <th>Total Size</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {monthly && monthly.length > 0 ? (
                  monthly.map((backup, index) => (
                    <tr key={backup.month || index}>
                      <td className="date-cell">
                        <CalendarRange size={14} />
                        {backup.month}
                      </td>
                      <td className="time-cell">
                        <Clock size={14} />
                        {formatDate(backup.lastModified)}
                      </td>
                      <td className="databases-cell">
                        {backup.databases.map((db, i) => (
                          <span key={i} className="db-badge">
                            <Database size={12} />
                            {db.name} ({db.sizeFormatted})
                          </span>
                        ))}
                      </td>
                      <td className="size-cell">{backup.totalSizeFormatted}</td>
                      <td className="status-cell">
                        <span className="status-badge status-success">
                          <CheckCircle2 size={14} /> Complete
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="empty-cell">
                      <Archive size={24} />
                      <span>No monthly backups yet (created on 1st of each month)</span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Manual Backups */}
        <motion.div className="backup-section" variants={itemVariants}>
          <div className="section-header">
            <h2><Tag size={20} /> Manual Backups</h2>
            <span className="badge badge-cyan">{manual?.length || 0} backups</span>
          </div>
          <div className="table-container">
            <table className="backup-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Name / Reason</th>
                  <th>Databases</th>
                  <th>Total Size</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {manual && manual.length > 0 ? (
                  manual.map((backup, index) => (
                    <tr key={`${backup.date}-${backup.customName || index}`}>
                      <td className="date-cell">
                        <Calendar size={14} />
                        {formatDate(backup.lastModified)}
                      </td>
                      <td className="name-cell">
                        {backup.customName ? (
                          <span className="backup-name-badge">
                            <FileText size={12} />
                            {backup.customName.replace(/-/g, ' ')}
                          </span>
                        ) : (
                          <span className="text-muted">No name</span>
                        )}
                      </td>
                      <td className="databases-cell">
                        {backup.databases.map((db, i) => (
                          <span key={i} className="db-badge">
                            <Database size={12} />
                            {db.name} ({db.sizeFormatted})
                          </span>
                        ))}
                      </td>
                      <td className="size-cell">{backup.totalSizeFormatted}</td>
                      <td className="status-cell">
                        <span className="status-badge status-success">
                          <CheckCircle2 size={14} /> Complete
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="empty-cell">
                      <Archive size={24} />
                      <span>No manual backups yet. Click "Run Backup Now" to create one.</span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>

      {/* Retention Info */}
      <motion.div className="retention-info" variants={itemVariants}>
        <div className="info-card">
          <h3><Timer size={16} /> Backup Schedule</h3>
          <p>Backups run automatically at <strong>2:00 AM daily</strong> via PM2 cron.</p>
        </div>
        <div className="info-card">
          <h3><Archive size={16} /> Retention Policy</h3>
          <ul>
            <li><CalendarDays size={14} /> Daily backups: <strong>30 days</strong></li>
            <li><CalendarRange size={14} /> Monthly backups: <strong>Indefinite</strong></li>
          </ul>
        </div>
        <div className="info-card">
          <h3><FolderArchive size={16} /> Storage Location</h3>
          <p>Azure Blob Storage: <code>callbot/logs/db-backups/</code></p>
        </div>
      </motion.div>

      {/* Backup Modal */}
      {showBackupModal && (
        <div className="backup-modal-overlay" onClick={() => setShowBackupModal(false)}>
          <div className="backup-modal" onClick={e => e.stopPropagation()}>
            <div className="backup-modal-header">
              <h3><UploadCloud size={20} /> Run Manual Backup</h3>
            </div>
            <div className="backup-modal-content">
              <p>Create a manual backup of the database. You can optionally provide a name or reason for this backup.</p>
              <div className="backup-name-input">
                <label>Backup Name (optional)</label>
                <input
                  type="text"
                  value={backupName}
                  onChange={e => setBackupName(e.target.value)}
                  placeholder="e.g., before-v4.0.2-migration"
                  maxLength={50}
                  autoFocus
                />
                <span className="input-hint">Use lowercase letters, numbers, and dashes. Max 50 characters.</span>
              </div>
            </div>
            <div className="backup-modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowBackupModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={triggerBackup} disabled={triggering}>
                <Play size={16} />
                {triggering ? 'Running...' : 'Start Backup'}
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  )
}

export default BackupStats
